import React, { useState, useEffect, useRef } from "react";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useParams, useNavigate } from "react-router-dom";
import debounce from 'lodash/debounce';
import WarehouseAssortmentDetailsComponent from "./component";

const CohortAssortmentDetailsContainer = (props) => {
  const navigate = useNavigate();
  const { assortmentId , reference_id} = useParams();

  const [assortmentDetails, setAssortmentDetails] = useState({});
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modifiedPrimaryItems, setModifiedPrimaryItems] = useState({});
  const [modifiedSecondaryItems, setModifiedSecondaryItems] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState(['All']);
  const [loadedHardwareTypes, setLoadedHardwareTypes] = useState(new Set());
  const [assortmentSlotDetails, setAssortmentSlotDetails] = useState();
  const [autoScrollDirection, setAutoScrollDirection] = useState(null);
  const [assortmentWarehouseMetaDataDetails, setAssortmentWarehouseMetaDataDetails] = useState(null);
  const [cohortData, setCohortData] = useState({
    reference_id: null,
    is_linked: false,
    reference_name: ''
  });

  const itemsContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  useEffect(() => {
    if (!assortmentId) {
      toastr.error("Assortment ID not found in URL");
      setLoading(false);
      return;
    }
    
    handleAssortmentCohortDetails();
    
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
    };
  }, []);

  const handleAssortmentCohortDetails = debounce(() => {
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/assortment/cohort/${reference_id}/metadata`,
      "GET",
      undefined,
      (response) => {
        setAssortmentWarehouseMetaDataDetails(response || null);
        
        const cohortInfo = response;
        if (cohortInfo) {
          setCohortData({
            reference_id: cohortInfo.reference_id,
            is_linked: cohortInfo.is_linked,
            reference_name: cohortInfo.reference_name
          });
          
          let apiUrl = `/partner/api/v1/assortment/${assortmentId}`;
          
          if (cohortInfo.is_linked) {
            apiUrl += `?isLinked=${cohortInfo.is_linked}&linkedCohortId=${reference_id}`;
          }
          
          props.getDataFromAPI(
            apiUrl,
            "GET",
            undefined,
            (response) => {
              setAssortmentDetails(response);
              extractCategories(response);
              setLoading(false);
            },
            (err) => {
              toastr.error(err.message || "Failed to fetch assortment details");
              setLoading(false);
            }
          );
        } else {
          setLoading(false);
        }
      },
      (err) => {
        setLoading(false);
      }
    );
  }, 300);

  const handleCheckRule = debounce(() => {
    setLoading(true);
    
    let apiUrl = `/partner/api/v1/assortment/${assortmentId}/rule/check`;
    
    if (cohortData.is_linked) {
      apiUrl += `?isLinked=${cohortData.is_linked}&linkedCohortId=${reference_id}`;
    }

    props.getDataFromAPI(
      apiUrl,
      "GET",
      undefined,
      (response) => {
        setAssortmentDetails((prev) => ({
          ...prev,
          rule_failures: response || []
        }));
        toastr.success("Rules rechecked successfully");
        setLoading(false);
      },
      (err) => {
        toastr.error(err.message || "Failed to check rules");
        setLoading(false);
      }
    );
  }, 300);

  const handleAssortmentSlotDetails = (hardwareTypeId) => {
    if (!hardwareTypeId) return;

    props.getDataFromAPI(
      `/partner/api/v1/assortment/hardwareType/${hardwareTypeId}/slot/detail`,
      "GET",
      undefined,
      (response) => {
        setAssortmentSlotDetails(response || []);
      },
      (err) => {
        setAssortmentSlotDetails([]);
      }
    );
  };

  const extractCategories = (data) => {
    if (!data) return;
    const allCategories = new Set();
    
    data.priority_wise_items?.forEach(item => {
      item.categories?.forEach(cat => allCategories.add(cat));
    });
    
    data.hardware_type_items?.forEach(hardware => {
      hardware.priority_wise_items?.forEach(item => {
        item.categories?.forEach(cat => allCategories.add(cat));
      });
      hardware.remaining_assortment_items?.forEach(item => {
        item.categories?.forEach(cat => allCategories.add(cat));
      });
    });
    
    setCategories(['All', ...Array.from(allCategories).sort()]);
  };

  const processItemsWithRanks = (items, startRank = 1) => {
    if (!items) return [];
    return items.map((item, index) => ({
      ...item,
      rank: startRank + index,
      originalRank: startRank + index,
      hasRankChanged: false
    }));
  };

  const filterItemsByCategory = (items) => {
    if (!items) return [];
    if (selectedCategory === 'All') return items;
    return items.filter(item => item.categories?.includes(selectedCategory));
  };

  const getCurrentItems = () => {
    let items = modifiedPrimaryItems[activeTabIndex];
    if (!items) {
      if (activeTabIndex === 0) {
        items = assortmentDetails.priority_wise_items || [];
      } else {
        const hardwareIndex = activeTabIndex - 1;
        items = assortmentDetails?.hardware_type_items?.[hardwareIndex]?.priority_wise_items || [];
      }
      items = processItemsWithRanks(items);
    }
    return filterItemsByCategory(items);
  };

  const getRemainingItems = () => {
    if (activeTabIndex === 0) return [];
    let items = modifiedSecondaryItems[activeTabIndex];
    if (!items) {
      const hardwareIndex = activeTabIndex - 1;
      items = assortmentDetails.hardware_type_items?.[hardwareIndex]?.remaining_assortment_items || [];
      items = processItemsWithRanks(items, getCurrentItems().length + 1);
    }
    return filterItemsByCategory(items);
  };

  const getSlotDetailsForItem = (item) => {
    if (activeTabIndex === 0) return null;
    const hardwareTypeId = assortmentDetails.hardware_type_items?.[activeTabIndex - 1]?.hardware_type_id;
    if (!hardwareTypeId) return null;

    const slots = assortmentSlotDetails?.[hardwareTypeId];
    if (!slots) return null;

    return slots.find(slot => slot.slotId === item.slotId);
  };

  const handleTabChange = (index) => {
    if (hasChanges) {
      if (window.confirm("You have unsaved changes. Do you want to discard them?")) {
        setActiveTabIndex(index);
        setHasChanges(false);
        setModifiedPrimaryItems({});
        setModifiedSecondaryItems({});
        
        if (index > 0 && !loadedHardwareTypes.has(index)) {
          const hardwareTypeId = assortmentDetails.hardware_type_items?.[index - 1]?.hardware_type_id;
          if (hardwareTypeId) {
            handleAssortmentSlotDetails(hardwareTypeId);
            setLoadedHardwareTypes(prev => new Set(prev).add(index));
          }
        }
      }
    } else {
      setActiveTabIndex(index);
      
      if (index > 0 && !loadedHardwareTypes.has(index)) {
        const hardwareTypeId = assortmentDetails.hardware_type_items?.[index - 1]?.hardware_type_id;
        if (hardwareTypeId) {
          handleAssortmentSlotDetails(hardwareTypeId);
          setLoadedHardwareTypes(prev => new Set(prev).add(index));
        }
      }
    }
  };

  const handleItemDrop = (droppedItem, targetIndex, sourceType) => {
    const currentPrimary = [...getCurrentItems()];
    const currentSecondary = [...getRemainingItems()];

    let newPrimary = [...currentPrimary];
    let newSecondary = [...currentSecondary];

    if (sourceType === 'primary') {
      const sourceIndex = newPrimary.findIndex(item => item.mv_id === droppedItem.mv_id);
      if (sourceIndex === -1 || sourceIndex === targetIndex) return;

      const [movedItem] = newPrimary.splice(sourceIndex, 1);
      newPrimary.splice(targetIndex, 0, movedItem);

      newPrimary = newPrimary.map((item, index) => ({
        ...item,
        rank: index + 1,
        hasRankChanged: item.mv_id === droppedItem.mv_id ? item.originalRank !== index + 1 : false
      }));
    } else {
      const sourceIndex = newSecondary.findIndex(item => item.mv_id === droppedItem.mv_id);
      if (sourceIndex === -1 || targetIndex >= newPrimary.length) return;

      const targetItem = newPrimary[targetIndex];
      const [movedItem] = newSecondary.splice(sourceIndex, 1);

      newPrimary[targetIndex] = {
        ...movedItem,
        rank: targetIndex + 1,
        hasRankChanged: true,
        originalRank: movedItem.originalRank
      };

      newSecondary.splice(sourceIndex, 0, {
        ...targetItem,
        rank: currentPrimary.length + sourceIndex + 1,
        hasRankChanged: true,
        originalRank: targetItem.rank
      });

      newSecondary = newSecondary.map((item, index) => ({
        ...item,
        rank: currentPrimary.length + index + 1,
        hasRankChanged: item.hasRankChanged
      }));
    }

    setModifiedPrimaryItems(prev => ({
      ...prev,
      [activeTabIndex]: newPrimary
    }));

    if (activeTabIndex > 0) {
      setModifiedSecondaryItems(prev => ({
        ...prev,
        [activeTabIndex]: newSecondary
      }));
    }

    setHasChanges(true);
  };

  const handleSaveChanges = () => {
    if (activeTabIndex === 0) {
      savePrimaryAssortment();
    } else {
      saveHardwareTypeAssortment();
    }
  };

  const savePrimaryAssortment = () => {
    const primaryItems = modifiedPrimaryItems[activeTabIndex];
    if (!primaryItems) return;

    const payload = {
      start_date: assortmentDetails.start_date,
      priority_wise_items: primaryItems.map(item => ({ mv_id: item.mv_id }))
    };

    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/assortment/${assortmentId}/update`,
      "PATCH",
      payload,
      (response) => {
        toastr.success("Primary assortment updated successfully");
        const updatedDetails = { ...assortmentDetails };
        updatedDetails.priority_wise_items = primaryItems.map(item => ({
          ...item,
          originalRank: item.rank,
          hasRankChanged: false
        }));
        setAssortmentDetails(updatedDetails);
        setHasChanges(false);
        setLoading(false);
      },
      (err) => {
        toastr.error(err.message);
        setLoading(false);
      }
    );
  };

  const saveHardwareTypeAssortment = () => {
    const hardwareTypeId = assortmentDetails.hardware_type_items?.[activeTabIndex - 1]?.hardware_type_id;
    if (!hardwareTypeId) return;

    const priorityItems = modifiedPrimaryItems[activeTabIndex]?.map(item => ({ mv_id: item.mv_id })) || [];
    const remainingItems = modifiedSecondaryItems[activeTabIndex]?.map(item => ({ mv_id: item.mv_id })) || [];
    const mergedPayload = [...priorityItems, ...remainingItems];

    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/assortment/${assortmentId}/hardwareType/${hardwareTypeId}/update`,
      "PUT",
      mergedPayload,
      (response) => {
        toastr.success("Hardware type assortment updated successfully");
        const updatedDetails = { ...assortmentDetails };
        const hardwareIndex = activeTabIndex - 1;

        updatedDetails.hardware_type_items[hardwareIndex].priority_wise_items =
          modifiedPrimaryItems[activeTabIndex].map(item => ({
            ...item,
            originalRank: item.rank,
            hasRankChanged: false
          }));

        updatedDetails.hardware_type_items[hardwareIndex].remaining_assortment_items =
          (modifiedSecondaryItems[activeTabIndex] || []).map(item => ({
            ...item,
            originalRank: item.rank,
            hasRankChanged: false
          }));

        setAssortmentDetails(updatedDetails);
        setHasChanges(false);
        setLoading(false);
      },
      (err) => {
        toastr.error(err.message || "Failed to update hardware type assortment");
        setLoading(false);
      }
    );
  };

  const handleCancelChanges = () => {
    setModifiedPrimaryItems({});
    setModifiedSecondaryItems({});
    setHasChanges(false);
  };

  const handleEndAssortment = () => {
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/assortment/${assortmentId}/inactive`,
      "PUT",
      undefined,
      (response) => {
        toastr.success("Assortment inactive successfully");
        setLoading(false);
        setShowDeleteModal(false);
        navigate(-1);
      },
      (err) => {
        toastr.error(err.message || "Something went wrong");
        setLoading(false);
        setShowDeleteModal(false);
      }
    );
  };

  const startAutoScroll = (direction) => {
    if (scrollIntervalRef.current) return;
    const scrollStep = direction === 'up' ? -20 : 20;
    scrollIntervalRef.current = setInterval(() => {
      if (itemsContainerRef.current) {
        const currentScroll = itemsContainerRef.current.scrollTop;
        const maxScroll = itemsContainerRef.current.scrollHeight - itemsContainerRef.current.clientHeight;

        if ((direction === 'up' && currentScroll > 0) ||
          (direction === 'down' && currentScroll < maxScroll)) {
          itemsContainerRef.current.scrollBy({
            top: scrollStep,
            behavior: 'smooth'
          });
        } else {
          stopAutoScroll();
        }
      }
    }, 50);
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    setAutoScrollDirection(null);
  };

  const handleDragUpdate = (e) => {
    if (!itemsContainerRef.current || !e.clientY) {
      stopAutoScroll();
      return;
    }

    const container = itemsContainerRef.current;
    const { top, bottom, height } = container.getBoundingClientRect();
    const clientY = e.clientY;
    const buffer = height * 0.2;

    if (clientY < top + buffer) {
      if (autoScrollDirection !== 'up') {
        stopAutoScroll();
        setAutoScrollDirection('up');
        startAutoScroll('up');
      }
    } else if (clientY > bottom - buffer) {
      if (autoScrollDirection !== 'down') {
        stopAutoScroll();
        setAutoScrollDirection('down');
        startAutoScroll('down');
      }
    } else {
      stopAutoScroll();
    }
  };

  return (
    <WarehouseAssortmentDetailsComponent
      assortmentDetails={assortmentDetails}
      loading={loading}
      activeTabIndex={activeTabIndex}
      onTabChange={handleTabChange}
      itemsContainerRef={itemsContainerRef}
      autoScrollDirection={autoScrollDirection}
      hasChanges={hasChanges}
      onSaveChanges={handleSaveChanges}
      onCancelChanges={handleCancelChanges}
      handleEndAssortment={handleEndAssortment}
      setShowDeleteModal={setShowDeleteModal}
      showDeleteModal={showDeleteModal}
      categories={categories}
      selectedCategory={selectedCategory}
      onCategoryChange={setSelectedCategory}
      currentItems={getCurrentItems()}
      remainingItems={getRemainingItems()}
      onItemDrop={handleItemDrop}
      onDragUpdate={handleDragUpdate}
      getSlotDetailsForItem={getSlotDetailsForItem}
      assortmentSlotDetails={assortmentSlotDetails}
      handleCheckRule={handleCheckRule}
    />
  );
};

export default connect(null, { getDataFromAPI })(CohortAssortmentDetailsContainer);